/**
 * TrainingHistoryPanel — the model home centerpiece: one thin olive line
 * over a faint grid climbing through the checkpoints that produced the
 * served model, with dashed reference levels and an ink marker on the
 * promoted one.
 *
 * Every point is a real eval score (`GET /api/models/{id}/checkpoints`) —
 * one distinct `model_slug` a pipeline pushed, scored on the SAME task set
 * as the model now serving, which is what makes the line a climb rather
 * than a walk across a shifting eval suite.
 *
 * Two things on this chart are drawn rather than measured, and both are
 * marked as such:
 *
 * - The x-axis is optimisation steps, but eval runs carry no step number.
 *   Checkpoints are PLACED on the axis from their real dates against the
 *   team's configured run length (`services/checkpoints.assign_steps`).
 * - The line between checkpoints is interpolated (`toTrainingCurve`), since
 *   three dots joined by straight segments read as if training teleported
 *   between evals.
 *
 * Only measured checkpoints get a dot, and the tooltip says which kind of
 * point you are on. When pipelines start pushing `metrics.step`, the
 * placement goes away and everything else here stands.
 */

import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  CHART_INK,
  CHART_REFERENCE_DASH,
  CHART_REFERENCE_STROKE,
} from '@/components/evals/chartStyle'
import { formatAxisTick, formatScore } from '@/components/evals/format'
import type { TrainingCurvePoint } from '@/components/models/home/contracts'
import { toTrainingCurve } from '@/components/models/home/contracts'
import type { CheckpointsResponse } from '@/api/generated/model'

interface TrainingHistoryPanelProps {
  checkpoints: CheckpointsResponse
}

function TrainingTooltip({
  active,
  payload,
  format,
}: {
  active?: boolean
  payload?: readonly { payload?: unknown }[]
  format: 'percent' | 'decimal'
}): React.JSX.Element | null {
  const point = payload?.[0]?.payload as TrainingCurvePoint | undefined
  if (!active || !point) return null
  return (
    <div className="border border-border bg-card p-2.5 text-xs">
      <p className="font-mono uppercase tracking-wider text-muted-foreground">
        {point.slug ?? `Step ${point.step.toLocaleString()}`}
      </p>
      <p className="mt-1">
        Eval score:{' '}
        <span className="font-mono tabular-nums">
          {formatScore(point.score, format)}
        </span>
      </p>
      {/* Interpolated points are labelled as such — the curve between
          checkpoints is drawn, and a tooltip that quoted a precise score
          there would read as a measurement. */}
      <p className="mt-1 text-muted-foreground">
        {point.slug === null
          ? 'Interpolated between checkpoints'
          : `Step ${point.step.toLocaleString()} · measured`}
      </p>
    </div>
  )
}

/**
 * A dot on measured checkpoints only — the interpolated points in between
 * are drawn values, and dotting them would present them as datapoints.
 */
function CheckpointDot(props: {
  cx?: number
  cy?: number
  payload?: TrainingCurvePoint
}): React.JSX.Element | null {
  const { cx, cy, payload } = props
  if (cx === undefined || cy === undefined || !payload?.slug) return null
  return <circle cx={cx} cy={cy} r={2.5} fill={CHART_INK} />
}

export function TrainingHistoryPanel({
  checkpoints,
}: TrainingHistoryPanelProps): React.JSX.Element {
  const format = checkpoints.score_format
  const points = toTrainingCurve(checkpoints)
  const promoted =
    checkpoints.checkpoints.find((c) => c.state === 'serving') ?? null

  if (points.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No comparable checkpoints yet. Push eval runs naming each one and they
        appear here.
      </p>
    )
  }

  // Give the highest reference line room to sit under the top gridline
  // instead of being clipped against it.
  const ceiling = Math.max(
    ...points.map((point) => point.score),
    ...checkpoints.reference_lines.map((line) => line.score),
  )
  const lastStep = points[points.length - 1]?.step ?? 0
  const top = Math.min(1, Math.ceil((ceiling + 0.05) * 10) / 10)

  return (
    <div>
      <div className="h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <LineChart data={points} margin={{ top: 16, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid stroke="hsl(var(--border) / 0.6)" />
            <XAxis
              dataKey="step"
              type="number"
              domain={[0, checkpoints.total_steps ?? lastStep]}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value: number) => value.toLocaleString()}
            />
            <YAxis
              domain={[0, top]}
              tickLine={false}
              axisLine={false}
              width={44}
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={(value: number) => formatAxisTick(value, format)}
            />
            <Tooltip
              content={({ active, payload }) => (
                <TrainingTooltip active={active} payload={payload} format={format} />
              )}
              cursor={{
                stroke: 'hsl(var(--muted-foreground) / 0.4)',
                strokeDasharray: '3 3',
              }}
            />
            {/* The base model, and the levels the team measures against.
                Curated on the training_history row, so there are two or
                three — few enough to label in place. */}
            {checkpoints.baseline_score !== null && (
              <ReferenceLine
                y={checkpoints.baseline_score}
                stroke={CHART_REFERENCE_STROKE}
                strokeDasharray={CHART_REFERENCE_DASH}
                label={{
                  value: `Base model ${formatScore(checkpoints.baseline_score, format)}`,
                  position: 'insideBottomRight',
                  fontSize: 10,
                  fill: 'hsl(var(--muted-foreground))',
                }}
              />
            )}
            {checkpoints.reference_lines.map((reference) => (
              <ReferenceLine
                key={reference.label}
                y={reference.score}
                stroke={CHART_REFERENCE_STROKE}
                strokeDasharray={CHART_REFERENCE_DASH}
                label={{
                  value: `${reference.label} ${formatScore(reference.score, format)}`,
                  position: 'insideBottomRight',
                  fontSize: 10,
                  fill: 'hsl(var(--muted-foreground))',
                }}
              />
            ))}
            {promoted?.step !== null && promoted !== null && (
              <ReferenceLine
                x={promoted.step}
                stroke="hsl(var(--foreground))"
                strokeWidth={1}
                label={{
                  value: 'Promoted',
                  position: 'insideTopLeft',
                  fontSize: 10,
                  fill: 'hsl(var(--foreground))',
                }}
              />
            )}
            {promoted?.step !== null && promoted !== null && (
              <ReferenceDot
                x={promoted.step}
                y={promoted.score}
                r={3}
                fill="hsl(var(--foreground))"
                stroke="none"
              />
            )}
            <Line
              dataKey="score"
              stroke={CHART_INK}
              strokeWidth={1.5}
              // Only MEASURED points get a dot. Dotting the interpolated
              // ones would present drawn values as data.
              dot={<CheckpointDot />}
              activeDot={{ r: 3, fill: CHART_INK, stroke: 'none' }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
